export const permissions = {
    app: {
        admin: ['*'],
        user: [],
    },
    org: {
        owner: [
            'create_project',
            'edit_project',
            'delete_project',
            'view_project',

            'manage_org_members',
            'invite_org_members',
            'invite_group_member',
            'manage_project_members',
            'make_creator',

            'view_org',
            'view_group',

            'update_org',
            'delete_org',

            'create_batch',
            'edit_batch',
            'delete_batch',

            'create_group',
            'edit_group',
            'delete_group',
            'manage_group',

            'manage_project',

            'view_project_submissions',
            'evaluate_milestone_submission',

            'manage_evaluation_rubric',

            'add_milestone',
            'edit_milestone',
            'delete_milestone',

            'accept_project_request',
            'reject_project_request',

            'add_milestone_submission',
            'delete_milestone_submission',

            'create_task',
            'edit_task',
            'view_task',
            'delete_task',
            'manage_task',
            'view_pow',
        ],
        admin: [
            'create_project',
            'edit_project',
            'delete_project',
            'view_project',

            'manage_org_members',
            'invite_org_members',
            'invite_group_member',
            'manage_project_members',
            'make_creator',

            'view_org',
            'view_group',

            'update_org',

            'create_batch',
            'edit_batch',
            'delete_batch',

            'create_group',
            'edit_group',
            'delete_group',
            'manage_group',

            'manage_project',

            'view_project_submissions',
            'evaluate_milestone_submission',

            'manage_evaluation_rubric',

            'add_milestone',
            'edit_milestone',
            'delete_milestone',

            'accept_project_request',
            'reject_project_request',

            'add_milestone_submission',
            'delete_milestone_submission',

            'create_task',
            'edit_task',
            'view_task',
            'delete_task',
            'manage_task',
            'view_pow',
        ],
        member: [
            'view_org',
            'view_project',
            'view_group',
            'view_task',
            'view_project_submissions',
        ],
    },
    project: {
        creator: [
            'edit_project',
            'view_project',
            'view_group',

            'manage_project_members',
            'make_creator',

            'create_group',
            'edit_group',
            'delete_group',
            'manage_group',

            'manage_project',

            'view_project_submissions',
            'evaluate_milestone_submission',

            'manage_evaluation_rubric',

            'add_milestone',
            'edit_milestone',
            'delete_milestone',

            'accept_project_request',
            'reject_project_request',

            'invite_group_member',

            'add_milestone_submission',
            'delete_milestone_submission',

            'create_task',
            'edit_task',
            'view_task',
            'delete_task',
            'manage_task',
            'view_pow',
        ],
        admin: [
            'edit_project',
            'view_project',
            'view_group',

            'manage_project_members',
            'create_group',
            'edit_group',
            'delete_group',
            'manage_group',

            'manage_project',

            'view_project_submissions',
            'evaluate_milestone_submission',

            'manage_evaluation_rubric',

            'add_milestone',
            'edit_milestone',
            'delete_milestone',

            'accept_project_request',
            'reject_project_request',

            'invite_group_member',

            'add_milestone_submission',
            'delete_milestone_submission',

            'create_task',
            'edit_task',
            'view_task',
            'delete_task',
            'manage_task',
            'view_pow',
        ],
        moderator: [
            'edit_project',
            'view_project',
            'view_group',
            'manage_group',

            'manage_project_members',
            'create_group',
            'edit_group',
            'delete_group',

            'manage_project',

            'view_project_submissions',
            'evaluate_milestone_submission',

            'manage_evaluation_rubric',

            'add_milestone',
            'edit_milestone',
            'delete_milestone',

            'accept_project_request',
            'reject_project_request',

            'invite_group_member',

            'add_milestone_submission',
            'delete_milestone_submission',

            'create_task',
            'edit_task',
            'view_task',
            'delete_task',
            'manage_task',
            'view_pow',
        ],
        evaluator: [
            'view_project',
            'view_group',
            'view_task',

            'view_project_submissions',
            'evaluate_milestone_submission',
            'view_pow',
        ],
        member: [
            'view_project',
            'view_group',
            'view_task',

            //'invite_group_member',
        ],
    },
    group: {
        creator: [
            'edit_group',
            'view_group',
            'view_task',
            'invite_group_member',

            'add_milestone_submission',
            'delete_milestone_submission',

            'create_task',
            'edit_task',
            'delete_task',
            'manage_task', //task management & quick actions & task submission
            'view_pow',
        ],
        member: [
            'edit_group',
            'view_group',
            'view_task',

            'add_milestone_submission',
            'delete_milestone_submission',

            'create_task',
            'edit_task',
            'delete_task',
            'manage_task',
            'view_pow',
        ],
    },
} as const;

// Fixed type extraction - this properly extracts all permission strings
type ExtractPermissions<T> =
    T extends Record<string, Record<string, readonly (infer U)[]>> ? U : never;

export type PermissionAction = ExtractPermissions<typeof permissions>;
export type PermissionContext = keyof typeof permissions;
export type AppRole = keyof typeof permissions.app;
export type OrgRole = keyof typeof permissions.org;
export type ProjectRole = keyof typeof permissions.project;
export type GroupRole = keyof typeof permissions.group;

// Check if a specific role has permission for an action
export function hasPermission(
    context: PermissionContext,
    role: string,
    action: PermissionAction,
): boolean {
    const contextPermissions = permissions[context];

    // Type-safe way to check if role exists in context
    const validRoles = Object.keys(contextPermissions) as Array<
        keyof typeof contextPermissions
    >;
    if (validRoles.includes(role as keyof typeof contextPermissions)) {
        const rolePermissions =
            contextPermissions[role as keyof typeof contextPermissions];
        // Check for wildcard permission (for app admin)
        return (
            (rolePermissions as readonly string[]).includes('*') ||
            (rolePermissions as readonly string[]).includes(action)
        );
    }
    return false;
}

// Helper function to check multiple roles
export function hasAnyPermission(
    context: PermissionContext,
    roles: string[],
    action: PermissionAction,
): boolean {
    return roles.some((role) => hasPermission(context, role, action));
}

// Get all permissions for multiple roles in a context
export function getAllPermissions(
    context: PermissionContext,
    roles: string[],
): string[] {
    const allPermissions = new Set<string>();

    roles.forEach((role) => {
        const contextPermissions = permissions[context];
        const validRoles = Object.keys(contextPermissions) as Array<
            keyof typeof contextPermissions
        >;

        if (validRoles.includes(role as keyof typeof contextPermissions)) {
            const rolePermissions =
                contextPermissions[role as keyof typeof contextPermissions];
            (rolePermissions as readonly string[]).forEach((permission) => {
                allPermissions.add(permission);
            });
        }
    });

    return Array.from(allPermissions);
}
